import { IAnalysisReport } from './InsightsModal';

/**
 * Maps MIME types to Blob data for clipboard operations
 */
interface IClipboardItemData {
	[mimeType: string]: Blob;
}

/**
 * Type declaration for the modern Clipboard API's ClipboardItem class.
 * Allows writing rich content (HTML + plain text) to the clipboard.
 */
export declare class ClipboardItem 
{
	public constructor(data: IClipboardItemData);
}

/**
 * Extended clipboard interface that includes the modern write() method
 * for copying both HTML and plain text formats simultaneously
 */
export interface IExtendedClipboard extends Clipboard {
	write(data: ClipboardItem[]): Promise<void>;
}

/**
 * Escapes HTML special characters to prevent injection when copying to clipboard
 * @param text - Text to escape
 * @returns HTML-safe string
 */
export const escapeHtml = (text: string | undefined | null): string => 
{
	if(!text) return '';
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#039;');
};

/**
 * Copies field insights to clipboard in both HTML and plain text formats.
 * Rich applications (Outlook, Word) will use HTML; others fall back to plain text.
 * @param insight - The analysis report to copy
 * @param source - Source identifier (provider.model) to include in copied content
 */
export const copyInsightsToClipboard = async (insight: IAnalysisReport | null, source: string, subject: string): Promise<void> =>
{
	if (!insight) return;

	const plainText = `Overview:
${insight.overview}

${insight.concerns.map((item, index) => `Concern ${index + 1} (${item.severity}):
Category: ${item.category}
Headline: ${item.headline}
Details: ${item.details}
Recommendation: ${item.recommendation}
`).join('\n')}`;

	const htmlContent = `
	<strong>${subject} Insights - ${escapeHtml(source)}</strong>
	<br/><br/>
	<strong style="color: #666; margin-bottom: 8px;">Overview</strong><br/>
	${escapeHtml(insight.overview)}<br/>
	<br/>
	${insight.concerns.map((item, index) => `
		<strong>[${escapeHtml(item.severity.toUpperCase())}] ${escapeHtml(item.headline)}</strong>
		<br/>
		<strong>Category:</strong> ${escapeHtml(item.category)}
		<br/>
		<strong>Details:</strong> <br/>
		${escapeHtml(item.details)}<br/>
		<strong>Recommendation:</strong><br/>
		${escapeHtml(item.recommendation)}<br/>
		<br/>
	`).join('')}
`;

	try 
	{
		// Try modern Clipboard API first for rich content (HTML + plain text)
		const clipboardExtended = navigator.clipboard as IExtendedClipboard;
		if (clipboardExtended && clipboardExtended.write) 
		{
			const htmlBlob = new Blob([htmlContent], { type: 'text/html' });
			const textBlob = new Blob([plainText], { type: 'text/plain' });
			
			await clipboardExtended.write([
				new ClipboardItem({
					'text/html': htmlBlob,
					'text/plain': textBlob
				})
			]);
		} 
		else 
		{
			// Fallback to plain text if modern API unavailable
			await navigator.clipboard.writeText(plainText);
		}
	} 
	catch (error) 
	{
		console.error('Failed to copy to clipboard:', error);
		// Final fallback to plain text on any error
		await navigator.clipboard.writeText(plainText);
	}
};
